import type { RedirectLoginOptions } from '@auth0/auth0-spa-js'
import { action, observable } from 'mobx'
import QS from 'qs'
import { interval, merge, type Observable, Subject } from 'rxjs'
import {
  distinctUntilChanged,
  first,
  map,
  switchMap,
  take,
} from 'rxjs/operators'
import type { GetLocationFn, NavigateFn } from '@/RootStore'
import type { GetAuthTokenDelegate } from '@/features/shell/platform/types'
import { toStream } from '@/utils/mobx-util'
import { isNotNil } from '@/utils/object-util'
import { filterNonNil, shareLatest } from '@/utils/rx-util'
import { memoize } from '@/utils/memoize-util'
import { isAbortError } from '@/utils/abort-util'
import { Auth0Provider } from '@/features/iam/auth/providers/Auth0Provider'
import { MockTokenProvider } from '@/features/iam/auth/providers/MockTokenProvider'
import { NativeTokenProvider } from '@/features/iam/auth/providers/NativeTokenProvider'

export interface SessionInfo {
  token: string
  authId: string

  /**
   * Whether the user has enrolled in multi-factor authentication.
   */
  mfaEnabled: boolean
}

export type AuthInitialScreen = 'login' | 'signUp'

export interface AuthStoreOptions {
  /**
   * The provider to authenticate with. Use {@link createAuthProvider} to
   * create one.
   */
  provider: AuthProvider

  /**
   * Navigates within the app, used to restore the URL after a login redirect.
   */
  navigate: NavigateFn

  /**
   * Gets the current location, saved as the return URL before a login.
   */
  getLocation: GetLocationFn

  /**
   * Emits when the application becomes active; triggers a session refresh.
   */
  becomeActive$: Observable<unknown>

  /**
   * Ends the user's session on the server.
   */
  userLogout(): Promise<void>

  /**
   * Gets the URL to redirect to after logging out, if the workspace has one.
   */
  getLogoutUrl(): string | undefined

  /**
   * Whether logging out must also end the session with the identity provider.
   */
  isFederatedLogout(): boolean

  /**
   * Authenticates the session's user with the given token.
   */
  authenticate(session: SessionInfo, signal?: AbortSignal): Promise<void>
}

export class AuthStore {
  /**
   * The token subject which emits when user authenticates or new token is refreshed
   */
  private readonly _sessionSubject = new Subject<SessionInfo | null>()

  /**
   * Subject that emits when logout is initiated (before redirect).
   */
  private readonly _logoutSubject$ = new Subject<void>()

  /**
   * The outcome of the authentication check, `null` until it has run.
   */
  @observable private accessor _authenticated: boolean | null = null

  /**
   * Emits whether we're authenticated once the authentication check has run.
   * Does not run the check itself.
   */
  readonly checkAuthenticationDecision$ = toStream(
    () => this._authenticated,
    true,
  ).pipe(filterNonNil(), take(1))

  /**
   * Emits a refreshed session or `null` if we got logged out.
   *
   * @private
   */
  private readonly _sessionRefresh$ = this._sessionSubject.pipe(
    first(isNotNil),
    switchMap(() =>
      merge(this._options.becomeActive$, interval(10000)).pipe(
        switchMap(() =>
          this._options.provider.fetchSessionInfo().catch((err) => {
            console.error('Failed to refresh session in the background:', err)
            return null
          }),
        ),
      ),
    ),
  )

  /**
   * Session stream.
   */
  readonly session$ = merge(this._sessionSubject, this._sessionRefresh$).pipe(
    // We only care about changes.
    distinctUntilChanged((a, b) => a?.token === b?.token),
    // Remember the last value for late subscribers.
    shareLatest(),
  )

  /**
   * Emits when we log out (before redirect).
   */
  readonly logout$ = this._logoutSubject$.asObservable()

  /**
   * Emits when we log in elsewhere (another tab).
   * @private
   */
  private readonly _loggedInElsewhere$ = this.session$.pipe(
    first((v) => v === null),
    switchMap(() =>
      this._options.becomeActive$.pipe(
        switchMap(async () =>
          this._options.provider.fetchSessionInfo().catch((err) => {
            logAuthError(err)
            return null
          }),
        ),
        filterNonNil(),
      ),
    ),
  )

  /**
   * Emits when we should reload the page because we logged in somewhere else
   * (another tab). Logging out needs no reload: the provider's logout leaves
   * the page, and reloading first would land back on the logout route.
   */
  readonly reload$ = this._loggedInElsewhere$

  /**
   * The token.
   */
  readonly token$ = this.session$.pipe(
    map((s) => s?.token),
    filterNonNil(),
    distinctUntilChanged(),
  )

  constructor(private readonly _options: AuthStoreOptions) {
    this.checkAuthentication = memoize(this.checkAuthentication.bind(this), {
      promise: true,
      // Every caller shares the one check, whatever its signal.
      keySelector: () => 'checkAuthentication',
    })
  }

  /**
   * Redirects to the provider's login or signup screen.
   */
  redirectToAuthorize(
    initialScreen: AuthInitialScreen,
    opts: RedirectLoginOptions | undefined,
  ) {
    return this._options.provider.redirectToAuthorize(initialScreen, opts)
  }

  @action.bound
  async logout(url?: string | null) {
    this._logoutSubject$.next()
    const redirectUrl = url || this._options.getLogoutUrl()
    // Ending the identity provider session matters more than recording the
    // logout, so a failed request doesn't keep the user signed in.
    await this._options.userLogout().catch(logAuthError)
    void this._options.provider.logout({
      returnTo:
        window.location.origin +
        (redirectUrl ? `?redirect_to=${encodeURIComponent(redirectUrl)}` : ''),
      federated: this._options.isFederatedLogout(),
    })
  }

  /**
   * Checks whether we're authenticated.
   */
  async checkAuthentication(signal?: AbortSignal): Promise<boolean> {
    // If there is a code + state in the URL then attempt to exchange
    // it for a new token
    const { code, state } = QS.parse(window.location.search, {
      ignoreQueryPrefix: true,
    })

    // Only attempt to run Auth0's OAuth flow if the current
    // path is the root. Otherwise we might be interfering with
    // the Stripe OAuth.
    const isRoot = window.location.pathname === '/'
    if (isRoot && code && state) {
      // Temporary Firefox workaround
      // https://github.com/auth0/auth0-spa-js/blob/master/FAQ.md#why-do-i-get-error-invalid-state-in-firefox-when-refreshing-the-page-immediately-after-a-login
      window.location.hash = window.location.hash // eslint-disable-line no-self-assign

      try {
        await this._options.provider.handleRedirectCallback()

        // It was a login redirect, restore the url
        const returnUrl = localStorage.getItem('returnUrl')
        localStorage.removeItem('returnUrl')
        if (returnUrl) {
          this._options.navigate(returnUrl, { replace: true })
        }
      } catch (err) {
        console.error('Code exchange failed:', err)
        // Remove query string from URL to remove ?code=...&state=...
        this._options.navigate(this._options.getLocation().pathname, {
          replace: true,
        })
      }
    }

    localStorage.removeItem('returnUrl')

    try {
      // Gets a token from the cache or performs a background refresh
      const session = await this._options.provider.fetchSessionInfo()
      if (!session) {
        this._setUnauthenticated()
        return false
      }

      await this._options.authenticate(session, signal)

      this._sessionSubject.next(session)
      this._setAuthenticated(true)
      return true
    } catch (err) {
      if (isAbortError(err)) {
        throw err
      }
      logAuthError(err)
      this._setUnauthenticated()

      const message = err instanceof Error ? err.message : undefined
      if (message?.includes('already in use')) {
        throw new Error(message, { cause: err })
      }
      return false
    }
  }

  @action
  private _setAuthenticated(authenticated: boolean) {
    this._authenticated = authenticated
  }

  private _setUnauthenticated() {
    this._sessionSubject.next(null)
    this._setAuthenticated(false)
    // Save the current URL to local storage so we can redirect after login.
    this._saveCurrentURLToStorage()
  }

  private _saveCurrentURLToStorage() {
    const { pathname, search } = this._options.getLocation()

    // Check for URLs we dont want to redirect user back to
    if (pathname === '/' || pathname === '/logout') {
      localStorage.removeItem('returnUrl')
      return
    }

    const url = pathname + search
    localStorage.setItem('returnUrl', url)
  }
}

function logAuthError(err: unknown) {
  const code = (err as { error?: string } | null)?.error
  if (code !== 'login_required' && code !== 'missing_refresh_token') {
    console.error('Get token silently error:', err)
  }
}

/**
 * Abstraction used to control how authentication is handled.
 */
export interface AuthProvider {
  /**
   * Gets the current session, or `null` when not authenticated.
   */
  fetchSessionInfo(): Promise<SessionInfo | null>

  /**
   * Exchanges the code and state of a login redirect for a session.
   */
  handleRedirectCallback(): Promise<void>

  /**
   * Redirects to the provider's login or signup screen.
   */
  redirectToAuthorize(
    initialScreen: AuthInitialScreen,
    opts: RedirectLoginOptions | undefined,
  ): Promise<void>

  /**
   * Ends the provider's session, then returns to `returnTo`.
   */
  logout(opts: ProviderLogoutOptions): Promise<void>
}

/**
 * Options passed to {@link AuthProvider.logout}.
 */
export interface ProviderLogoutOptions {
  /**
   * The URL to return to once logged out.
   */
  returnTo: string

  /**
   * Whether to also end the session with the identity provider.
   */
  federated: boolean
}

/**
 * Options for {@link createAuthProvider}.
 */
export interface CreateAuthProviderOptions {
  /**
   * Gets the delegate for the token handed over by a native app shell.
   */
  getAuthTokenDelegate: GetAuthTokenDelegate
}

/**
 * Creates an auth provider.
 */
export function createAuthProvider({
  getAuthTokenDelegate,
}: CreateAuthProviderOptions): AuthProvider {
  return new MockTokenProvider(
    new NativeTokenProvider(getAuthTokenDelegate, Auth0Provider.create()),
  )
}
